import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { X, Printer, Truck, Sparkle, MapPin } from "@phosphor-icons/react";
import { getLabel } from "../lib/api";

const courierBg = {
  royal_mail: "bg-[#FFDF85]",
  evri: "bg-[#D4C4FB]",
};

export default function ShippingLabelModal({ orderId, onClose }) {
  const [label, setLabel] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await getLabel(orderId);
        if (!cancelled) setLabel(r);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        data-testid="label-modal"
        className={`nb-card max-h-[90vh] w-full max-w-lg overflow-y-auto ${
          label ? courierBg[label.courier] || "bg-white" : "bg-white"
        }`}
      >
        <div className="flex items-start justify-between">
          <div>
            <span className="nb-label">Shipping label</span>
            <h2 className="mt-1 font-display text-2xl font-black tracking-tight">
              {label?.courier_name || "Loading…"}
            </h2>
          </div>
          <button
            data-testid="close-label"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-lg border-[3px] border-[#0A0A0A] bg-white shadow-[3px_3px_0_0_#0A0A0A]"
          >
            <X weight="bold" size={16} />
          </button>
        </div>

        {loading || !label ? (
          <div className="mt-6 flex items-center gap-3">
            <Sparkle weight="duotone" size={20} className="animate-spin" />
            <span className="font-medium">Loading label…</span>
          </div>
        ) : (
          <>
            <p className="mt-4 text-sm font-medium leading-relaxed">{label.instructions}</p>

            <div className="mt-6 flex flex-col items-center rounded-xl border-[3px] border-[#0A0A0A] bg-white p-6">
              <div data-testid="label-qr" className="rounded-md border-[3px] border-[#0A0A0A] bg-white p-3">
                <QRCodeSVG value={label.tracking_code || label.label_ref || orderId} size={196} level="M" />
              </div>
              <div data-testid="label-tracking" className="mt-4 font-mono text-base font-black tracking-widest">
                {label.tracking_code}
              </div>
              <div className="mt-1 text-[10px] font-bold uppercase tracking-[0.25em] text-[#4A4A4A]">
                Ref · {label.label_ref}
              </div>
              <div className="mt-4 grid w-full grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-[#4A4A4A]">Courier</div>
                  <div className="flex items-center gap-1 font-display font-bold">
                    <Truck weight="bold" size={14} /> {label.courier_name}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-[#4A4A4A]">ETA</div>
                  <div className="font-display font-bold">{label.eta || "—"}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-[#4A4A4A]">Send to</div>
                  <div className="flex items-start gap-1 font-medium">
                    <MapPin weight="bold" size={14} className="mt-0.5" />
                    <div>
                      <div className="font-bold">{label.buyer_name}</div>
                      <div className="text-sm">{label.shipping_address || "Buyer to provide address"}</div>
                    </div>
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-[#4A4A4A]">Item</div>
                  <div className="font-display font-bold">{label.item_title}</div>
                </div>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-3">
              {label.label_url && (
                <a
                  data-testid="open-label-pdf"
                  href={label.label_url}
                  target="_blank"
                  rel="noreferrer"
                  className="nb-btn nb-btn-secondary inline-flex items-center gap-2"
                >
                  <Printer weight="bold" size={18} /> Open courier PDF
                </a>
              )}
              <button
                data-testid="print-label"
                onClick={() => window.print()}
                className="nb-btn inline-flex items-center gap-2"
              >
                <Printer weight="bold" size={18} /> Print this page
              </button>
              <button onClick={onClose} className="nb-btn nb-btn-ghost">Close</button>
            </div>
            {label.sandbox && (
              <p data-testid="label-sandbox" className="mt-3 text-[10px] font-bold uppercase tracking-[0.2em] text-[#4A4A4A]">
                Sandbox mode — real {label.courier_name} labels activate once API credentials are configured
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
